import OutboundDealsApi from 'api/admins/outbound-deals-api'
import TitlePage from 'components/atoms/title-page'
import CsvUploadCard from 'components/organisms/csv-upload-card'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useAuth } from 'providers/auth-provider'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SOLD_REPORT_ALLOWED_ROLES = ['owner', 'admin', 'warehouse', 'finance']
const SOLD_REPORT_ALLOWED_EMAILS = ['sweta.sabilillah.belleandkate@gmail.com']

const CUSTOMER_SAMPLE_CSV = [
  'nama_cust',
  'margareth theresia',
  'faustine.katrini@gmail.com',
  'jocelyn josephine'
].join('\n')
const CUSTOMER_SAMPLE_CSV_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(CUSTOMER_SAMPLE_CSV)}`

const downloadCsvBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([ blob ], { type: 'text/csv;charset=utf-8;' }))
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.parentNode.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const OutboundSoldItemsCsvReportPage = ({ pageUtils }) => {
  const [ customerCsvFile, setCustomerCsvFile ] = useState(null)
  const [ preview, setPreview ] = useState(null)
  const [ isPreviewing, setIsPreviewing ] = useState(false)
  const [ isDownloading, setIsDownloading ] = useState(false)
  const [ dateDealCreatedFrom, setDateDealCreatedFrom ] = useState('')
  const [ dateDealCreatedTo, setDateDealCreatedTo ] = useState('')

  const history = useHistory()
  const { user } = useAuth()
  const currentRole = user?.role?.toString().trim().toLowerCase().replace(/\s+/g, '_') || ''
  const currentEmail = user?.email?.toString().trim().toLowerCase() || ''
  const canAccess = SOLD_REPORT_ALLOWED_ROLES.includes(currentRole) || SOLD_REPORT_ALLOWED_EMAILS.includes(currentEmail)
  const previewRows = preview?.rows || []
  const summary = preview?.summary || {}
  const unmatchedCustomers = preview?.unmatched_customers || []
  const pricesIncluded = summary.prices_included === true

  useEffect(() => {
    if (!canAccess) history.replace('/app/outbound/deals/list')
  }, [ canAccess, history ])

  const handleApiError = (error) => {
    const data = error?.response?.data

    if (data instanceof Blob) {
      data.text().then((text) => {
        try {
          pageUtils.setApiErrorMsg(JSON.parse(text))
        } catch (_) {
          pageUtils.setAlertMsg(text || 'Sold item CSV report failed.', ALERT_TYPES.DANGER)
        }
      })
      return
    }

    pageUtils.setApiErrorMsg(data || { message: 'Sold item CSV report failed.' })
  }

  const validateDateRange = () => {
    if (!dateDealCreatedFrom || !dateDealCreatedTo) {
      pageUtils.setAlertMsg('Please select Deal Created From and Deal Created To first.', ALERT_TYPES.WARNING)
      return false
    }

    if (dateDealCreatedFrom > dateDealCreatedTo) {
      pageUtils.setAlertMsg('Deal Created From cannot be after Deal Created To.', ALERT_TYPES.WARNING)
      return false
    }

    return true
  }

  const reportOptions = () => ({
    date_deal_created_from : dateDealCreatedFrom,
    date_deal_created_to   : dateDealCreatedTo,
    tz                     : 'Asia/Jakarta'
  })

  const resetPreview = () => {
    setCustomerCsvFile(null)
    setPreview(null)
  }

  const handleDateFromChange = (event) => {
    setDateDealCreatedFrom(event.target.value)
    resetPreview()
  }

  const handleDateToChange = (event) => {
    setDateDealCreatedTo(event.target.value)
    resetPreview()
  }

  const handleUploadCustomerCsv = (file) => {
    if (!validateDateRange()) return

    setIsPreviewing(true)
    setCustomerCsvFile(file)

    OutboundDealsApi.previewSoldItemsCsv(file, reportOptions()).then((response) => {
      setPreview(response.data)
      pageUtils.setAlertMsg('Customer CSV matched. Check preview, then download the output CSV.', ALERT_TYPES.SUCCESS)
    }).catch((error) => {
      setCustomerCsvFile(null)
      setPreview(null)
      handleApiError(error)
    }).finally(() => setIsPreviewing(false))
  }

  const handleDownloadOutput = () => {
    if (!customerCsvFile) {
      pageUtils.setAlertMsg('Upload customer CSV first.', ALERT_TYPES.WARNING)
      return
    }

    if (!validateDateRange()) return

    setIsDownloading(true)
    OutboundDealsApi.exportSoldItemsCsv(customerCsvFile, reportOptions()).then((response) => {
      const fromLabel = dateDealCreatedFrom.replace(/-/g, '')
      const toLabel = dateDealCreatedTo.replace(/-/g, '')
      const filename = `outbound_sold_items_${fromLabel}-${toLabel}.csv`

      downloadCsvBlob(response.data, filename)
      pageUtils.setAlertMsg('Output CSV downloaded successfully.', ALERT_TYPES.SUCCESS)
    }).catch(handleApiError)
      .finally(() => setIsDownloading(false))
  }

  const handleBackToOrderList = () => {
    history.push('/app/outbound/deals/list')
  }

  if (!canAccess) return null

  return (
    <>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <TitlePage mainTitle={ 'Outbound Sold Item CSV Report' } />
        <Button color='secondary' outline onClick={ handleBackToOrderList }>
          { 'Back to Order List' }
        </Button>
      </div>

      <Card className='shadow-sm border-0'>
        <CardHeader className='bg-white'>
          <Row className='align-items-center'>
            <Col md={ 8 }>
              <h3 className='mb-1'>{ 'Outbound Sold Item CSV Report' }</h3>
              <p className='text-muted mb-0'>
                { 'Upload nama customer, pilih range Deal Created Date, preview hasil matching, lalu download CSV per SKU / item untuk kebutuhan pickup dan backup Warehouse.' }
              </p>
            </Col>
            <Col md={ 4 } className='text-md-right mt-3 mt-md-0'>
              <a
                className='btn btn-outline-primary'
                href={ CUSTOMER_SAMPLE_CSV_URL }
                download='outbound-sold-items-customer-template.csv'
              >
                { 'Download CSV Template' }
              </a>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <Alert color='info'>
            <strong>{ 'Input: ' }</strong>
            <code>{ 'nama_cust' }</code>
            { ' + Deal Created From/To. Nama customer dapat berupa customer name atau email.' }
            <div className='mt-1'>
              { 'Output dibuat satu row per SKU/cart item dan berisi condition, brand, product name, quantity sold, tanggal beli, kelengkapan, serta lokasi item/box/inclusions jika tersedia.' }
            </div>
          </Alert>

          <Row>
            <Col lg={ 5 }>
              <Row form>
                <Col md={ 6 }>
                  <FormGroup>
                    <Label for='outbound-sold-items-date-from'>{ 'Deal Created From' }</Label>
                    <Input
                      id='outbound-sold-items-date-from'
                      type='date'
                      value={ dateDealCreatedFrom }
                      onChange={ handleDateFromChange }
                    />
                  </FormGroup>
                </Col>
                <Col md={ 6 }>
                  <FormGroup>
                    <Label for='outbound-sold-items-date-to'>{ 'Deal Created To' }</Label>
                    <Input
                      id='outbound-sold-items-date-to'
                      type='date'
                      value={ dateDealCreatedTo }
                      onChange={ handleDateToChange }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div className='table-responsive mb-3'>
                <Table bordered size='sm' className='mb-0'>
                  <thead>
                    <tr>
                      <th>{ 'Header' }</th>
                      <th>{ 'Required?' }</th>
                      <th>{ 'Example' }</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>{ 'nama_cust' }</code></td>
                      <td>{ 'Yes' }</td>
                      <td><code>{ 'margareth theresia' }</code></td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <pre className='bg-light border rounded p-3 mb-3 small'>
                { CUSTOMER_SAMPLE_CSV }
              </pre>

              <CsvUploadCard
                label='Upload Customer CSV / XLSX'
                onFileUpload={ handleUploadCustomerCsv }
                isUploading={ isPreviewing }
              />

              {
                unmatchedCustomers.length > 0 && (
                  <Alert color='warning' className='mt-3 mb-0'>
                    <strong>{ `Unmatched customers (${unmatchedCustomers.length}): ` }</strong>
                    { unmatchedCustomers.slice(0, 20).join(', ') }
                    { unmatchedCustomers.length > 20 ? ' ...' : '' }
                    <div className='mt-1'>
                      <strong>{ 'Please review customer name again.' }</strong>
                      { ' Matching checks the Deal customer detail, all saved user details, email, and a conservative one-letter correction at the end of each name word.' }
                    </div>
                  </Alert>
                )
              }
            </Col>

            <Col lg={ 7 }>
              <Row className='mb-3'>
                <Col sm={ 3 }>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Input Customers' }</div>
                    <h3 className='mb-0'>{ summary.input_customers || 0 }</h3>
                  </div>
                </Col>
                <Col sm={ 3 } className='mt-3 mt-sm-0'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Matched' }</div>
                    <h3 className='mb-0'>{ summary.matched_customers || 0 }</h3>
                  </div>
                </Col>
                <Col sm={ 3 } className='mt-3 mt-sm-0'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Deals' }</div>
                    <h3 className='mb-0'>{ summary.matched_deals || 0 }</h3>
                  </div>
                </Col>
                <Col sm={ 3 } className='mt-3 mt-sm-0'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Item Rows' }</div>
                    <h3 className='mb-0'>{ summary.item_rows || 0 }</h3>
                  </div>
                </Col>
              </Row>

              <div className='d-flex justify-content-between align-items-center mb-2'>
                <div>
                  <h4 className='mb-0'>{ 'CSV Preview Result' }</h4>
                  {
                    summary.preview_truncated && (
                      <small className='text-muted'>
                        { `Showing first ${summary.preview_limit || 100} of ${summary.item_rows || 0} rows.` }
                      </small>
                    )
                  }
                </div>
                <Button
                  color='success'
                  disabled={ !customerCsvFile || !preview || isDownloading || summary.item_rows === 0 }
                  onClick={ handleDownloadOutput }
                >
                  { isDownloading ? 'Generating...' : 'Download Output CSV' }
                </Button>
              </div>

              <div className='table-responsive' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <Table bordered hover size='sm' className='mb-0'>
                  <thead className='thead-light'>
                    <tr>
                      <th>{ 'Customer' }</th>
                      <th>{ 'SKU' }</th>
                      <th>{ 'Condition' }</th>
                      <th>{ 'Brand' }</th>
                      <th>{ 'Product Name' }</th>
                      { pricesIncluded && <th>{ 'Default Price' }</th> }
                      { pricesIncluded && <th>{ 'Selling Price' }</th> }
                      { pricesIncluded && <th>{ 'Sales Price' }</th> }
                      <th>{ 'Kelengkapan' }</th>
                      <th>{ 'Box?' }</th>
                      <th>{ 'Box Location' }</th>
                      <th>{ 'Qty' }</th>
                      <th>{ 'Tanggal Beli' }</th>
                      <th>{ 'Deal' }</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      previewRows.length > 0 ? previewRows.map((row, index) => (
                        <tr key={ `${row.deal_reference}-${row.sku}-${index}` }>
                          <td>{ row.nama_customer || row.input_customer || '-' }</td>
                          <td><code>{ row.sku || '-' }</code></td>
                          <td>{ row.condition || '-' }</td>
                          <td>{ row.brand || '-' }</td>
                          <td>{ row.item_name || '-' }</td>
                          { pricesIncluded && <td>{ row.default_price || '-' }</td> }
                          { pricesIncluded && <td>{ row.selling_price || '-' }</td> }
                          { pricesIncluded && <td>{ row.sales_price || '-' }</td> }
                          <td style={{ minWidth: '180px' }}>{ row.kelengkapan || '-' }</td>
                          <td>
                            <Badge color={ row.box_status === 'YES' ? 'success' : row.box_status === 'NO' ? 'danger' : 'secondary' }>
                              { row.box_status || '-' }
                            </Badge>
                          </td>
                          <td>{ row.box_location || '-' }</td>
                          <td>{ row.qty_sold || 0 }</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{ row.tanggal_beli || '-' }</td>
                          <td><code>{ row.deal_reference || '-' }</code></td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={ pricesIncluded ? 14 : 11 } className='text-center text-muted py-4'>
                            { 'Pilih date range dan upload customer CSV untuk melihat preview di sini.' }
                          </td>
                        </tr>
                      )
                    }
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  )
}

OutboundSoldItemsCsvReportPage.propTypes = propTypes

export default OutboundSoldItemsCsvReportPage
